/**
===================================================
Module: frontend.src.components.AdminNotifications.tsx
Description: Admin panel for managing system notifications
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
===================================================
*/

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import TopBar from "./TopBar";
import NotificationSystem, { Notification as Toast } from "./NotificationSystem";
import "../styles/AdminUsers.css";
import "../styles/AdminNotifications.css";

interface SystemNotification {
  id: number;
  title: string;
  content: string;
  severity: "info" | "warning" | "critical";
  display_start: string;
  display_end: string;
  is_active: boolean;
  status: "active" | "scheduled" | "expired" | "inactive";
  created_by: string;
  creator_email: string;
  created_at: string;
  updated_at: string;
}

interface NotificationFormData {
  title: string;
  content: string;
  severity: "info" | "warning" | "critical";
  display_start: string;
  display_end: string;
  is_active: boolean;
}

const emptyForm: NotificationFormData = {
  title: "",
  content: "",
  severity: "info",
  display_start: "",
  display_end: "",
  is_active: true,
};

const AdminNotifications: React.FC = () => {
  const { t } = useTranslation("admin");
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<NotificationFormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const addToast = (toast: Omit<Toast, "id">) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  };
  const removeToast = (id: string) =>
    setToasts((prev) => prev.filter((n) => n.id !== id));

  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAdminNotifications(page);
      setNotifications(response.notifications || []);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      addToast({ type: "error", message: t("notif.loadError", { defaultValue: "Failed to load notifications" }) });
    } finally {
      setIsLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleEdit = (n: SystemNotification) => {
    setEditingId(n.id);
    setFormData({
      title: n.title,
      content: n.content,
      severity: n.severity,
      display_start: n.display_start ? n.display_start.slice(0, 16) : "",
      display_end: n.display_end ? n.display_end.slice(0, 16) : "",
      is_active: n.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      addToast({ type: "error", message: t("notif.titleRequired", { defaultValue: "Title is required" }) });
      return;
    }
    if (!formData.content.trim()) {
      addToast({ type: "error", message: t("notif.contentRequired", { defaultValue: "Content is required" }) });
      return;
    }
    if (!formData.display_start || !formData.display_end) {
      addToast({ type: "error", message: t("notif.datesRequired", { defaultValue: "Display dates are required" }) });
      return;
    }

    try {
      setIsSaving(true);
      if (editingId) {
        await apiService.updateNotification(editingId, formData);
        addToast({ type: "success", message: t("notif.updated", { defaultValue: "Notification updated" }), duration: 3000 });
      } else {
        await apiService.createNotification({
          title: formData.title,
          content: formData.content,
          severity: formData.severity,
          display_start: formData.display_start,
          display_end: formData.display_end,
        });
        addToast({ type: "success", message: t("notif.created", { defaultValue: "Notification created" }), duration: 3000 });
      }
      setShowModal(false);
      loadNotifications();
    } catch (err) {
      addToast({ type: "error", message: t("notif.saveError", { defaultValue: "Failed to save notification" }) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteNotification(id);
      addToast({ type: "success", message: t("notif.deleted", { defaultValue: "Notification deleted" }), duration: 3000 });
      setDeleteConfirmId(null);
      loadNotifications();
    } catch (err) {
      addToast({ type: "error", message: t("notif.deleteError", { defaultValue: "Failed to delete notification" }) });
    }
  };

  const severityBadge = (severity: string) => {
    const cls = `severity-badge severity-${severity}`;
    const label =
      severity === "critical"
        ? t("notif.severity.critical", { defaultValue: "Critical" })
        : severity === "warning"
        ? t("notif.severity.warning", { defaultValue: "Warning" })
        : t("notif.severity.info", { defaultValue: "Info" });
    return <span className={cls}>{label}</span>;
  };

  const statusBadge = (status: string) => {
    const cls = `status-badge status-${status}`;
    const label =
      status === "active"
        ? t("notif.status.active", { defaultValue: "Active" })
        : status === "scheduled"
        ? t("notif.status.scheduled", { defaultValue: "Scheduled" })
        : status === "expired"
        ? t("notif.status.expired", { defaultValue: "Expired" })
        : t("notif.status.inactive", { defaultValue: "Inactive" });
    return <span className={cls}>{label}</span>;
  };

  const formatDate = (iso: string) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  return (
    <div className="admin-container">
      <div className="admin-content">
        <TopBar />

        <div className="admin-header-section">
          <div className="breadcrumb">
            <span className="breadcrumb-link" onClick={() => (window.location.href = "/projects")}>
              {t("common:home", { defaultValue: "Home" })}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-link" onClick={() => (window.location.href = "/admin")}>
              {t("title")}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span>{t("notif.title", { defaultValue: "Notifications" })}</span>
          </div>

          <div className="admin-title-row">
            <h1>{t("notif.title", { defaultValue: "お知らせ管理" })}</h1>
            <div className="header-actions">
              <button
                className="btn-secondary btn-with-icon"
                onClick={() => (window.location.href = "/admin/users")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                {t("title", { defaultValue: "Manage Users" })}
              </button>
              <button className="btn-primary btn-with-icon" onClick={handleCreate}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t("notif.create", { defaultValue: "新規作成" })}
              </button>
            </div>
          </div>
        </div>

        <div className="admin-main">
          {isLoading ? (
            <div className="admin-loading">{t("common:loading", { defaultValue: "Loading..." })}</div>
          ) : notifications.length === 0 ? (
            <div className="admin-empty">{t("notif.empty", { defaultValue: "No notifications" })}</div>
          ) : (
            <>
              <div className="notif-table-wrapper">
                <table className="notif-table">
                  <thead>
                    <tr>
                      <th>{t("notif.col.status", { defaultValue: "Status" })}</th>
                      <th>{t("notif.col.severity", { defaultValue: "Severity" })}</th>
                      <th>{t("notif.col.title", { defaultValue: "Title" })}</th>
                      <th>{t("notif.col.period", { defaultValue: "Display Period" })}</th>
                      <th>{t("notif.col.updated", { defaultValue: "Last Updated" })}</th>
                      <th>{t("notif.col.actions", { defaultValue: "Actions" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.map((n) => (
                      <tr key={n.id} className={`notif-row notif-row-${n.status}`}>
                        <td>{statusBadge(n.status)}</td>
                        <td>{severityBadge(n.severity)}</td>
                        <td className="notif-title-cell">
                          <div className="notif-title-text">{n.title}</div>
                        </td>
                        <td className="notif-period-cell">
                          {formatDate(n.display_start)}
                          <br />
                          〜 {formatDate(n.display_end)}
                        </td>
                        <td>{formatDate(n.updated_at)}</td>
                        <td className="notif-actions-cell">
                          <button className="btn-icon" title="Edit" onClick={() => handleEdit(n)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            className="btn-icon btn-icon-danger"
                            title="Delete"
                            onClick={() => setDeleteConfirmId(n.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="notif-pagination">
                  <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    ←
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="admin-modal notif-modal">
            <div className="modal-header">
              <h2>
                {editingId
                  ? t("notif.editTitle", { defaultValue: "お知らせ編集" })
                  : t("notif.createTitle", { defaultValue: "お知らせ新規作成" })}
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>

            <div className="admin-form-content">
              <div className="form-group">
                <label>{t("notif.form.title", { defaultValue: "タイトル" })}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t("notif.form.titlePlaceholder", { defaultValue: "Notification title" })}
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label>{t("notif.form.content", { defaultValue: "内容" })}</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={t("notif.form.contentPlaceholder", { defaultValue: "Notification details..." })}
                  rows={5}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("notif.form.severity", { defaultValue: "重要度" })}</label>
                  <select
                    value={formData.severity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        severity: e.target.value as "info" | "warning" | "critical",
                      })
                    }
                  >
                    <option value="info">{t("notif.severity.info", { defaultValue: "Info" })}</option>
                    <option value="warning">{t("notif.severity.warning", { defaultValue: "Warning" })}</option>
                    <option value="critical">{t("notif.severity.critical", { defaultValue: "Critical" })}</option>
                  </select>
                </div>

                {editingId && (
                  <div className="form-group">
                    <label>{t("notif.form.active", { defaultValue: "有効" })}</label>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <span>{formData.is_active ? t("notif.form.activeYes", { defaultValue: "有効" }) : t("notif.form.activeNo", { defaultValue: "無効" })}</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("notif.form.displayStart", { defaultValue: "表示開始" })}</label>
                  <input
                    type="datetime-local"
                    value={formData.display_start}
                    onChange={(e) => setFormData({ ...formData, display_start: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t("notif.form.displayEnd", { defaultValue: "表示終了" })}</label>
                  <input
                    type="datetime-local"
                    value={formData.display_end}
                    onChange={(e) => setFormData({ ...formData, display_end: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving
                  ? t("editUser.saving", { defaultValue: "Saving..." })
                  : t("editUser.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId !== null && (
        <div className="modal-overlay">
          <div className="admin-modal delete-modal">
            <div className="modal-header">
              <h2>{t("notif.deleteTitle", { defaultValue: "お知らせ削除" })}</h2>
              <button className="close-btn" onClick={() => setDeleteConfirmId(null)}>
                &times;
              </button>
            </div>
            <div className="admin-form-content">
              <p>{t("notif.deleteConfirm", { defaultValue: "Are you sure you want to delete this notification?" })}</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirmId(null)}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button className="btn-primary btn-danger" onClick={() => handleDelete(deleteConfirmId)}>
                {t("common:delete", { defaultValue: "Delete" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationSystem notifications={toasts} onRemove={removeToast} />
    </div>
  );
};

export default AdminNotifications;
